import { Button, Card, Col, Form, Input, InputNumber, Layout, message, Row, Typography } from "antd";
import { Descriptions } from "antd/es";
import { getData, postData } from "apiServices/common";
import AppPageHeader from "common/PageHeader";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useLocation } from "react-router-dom";

const UpdateFinishingHourlyIronOriginal = (props) => {
  const { type } = props;
  const [sewingDetails, setSewingDetails] = useState();
  const [sewingEntries, setSewingEntries] = useState();
  const [totalQuantity, setTotalQuantity] = useState(0);

  //   Routes
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();

  const searchParams = new URLSearchParams(location.search);
  const entry_date = searchParams.get("entry_date");
  const finishing_line_id = searchParams.get("finishing_line_id");
  const supervisor_id = searchParams.get("supervisor_id");
  const style_id = searchParams.get("style_id");
  const sales_contract_id = searchParams.get("sales_contract_id");
  const buying_office_id = searchParams.get("buying_office_id");
  const buyer_id = searchParams.get("buyer_id");
  const sub_contract_factory_id = searchParams.get("sub_contract_factory_id");
  const sub_style_id = searchParams.get("sub_style_id");
  const po_map_id = searchParams.get("po_map_id");
  const color_id = searchParams.get("color_id");
  const size_id = searchParams.get("size_id");

  const payload = {
    entry_date: dayjs(entry_date).format("YYYY-MM-DD"),
    finishing_line_id: finishing_line_id || null,
    supervisor_id: supervisor_id || null,
    style_id: style_id || null,
    sales_contract_id: sales_contract_id || null,
    buying_office_id: buying_office_id || null,
    buyer_id: buyer_id || null,
    sub_contract_factory_id: sub_contract_factory_id || null,
    sub_style_id: sub_style_id || null,
    po_map_id: po_map_id || null,
    color_id: color_id || null,
    size_id: size_id || null,
  };

  //   Antd Constants
  const [form] = Form.useForm();

  const handleSubmit = async (values) => {
    const details = values.sewingEntries.map((entry) => ({
      id: entry.id,
      quantity: entry.quantity,
    }));

    const payload = {
      total_qty: totalQuantity,
      details: details,
    };

    try {
      const response = await postData(`/api/digitalization/single-group-wise-${type}-update`, payload);
      console.log("🚀 ~ handleSubmit ~ response:", response);
      if (response?.code === 200) {
        message.success("Updated successfully");
        navigate(`/digitalization/finishing/hourly-${type}`);
      }
    } catch (error) {
      message.error("Failed to update");
    }
  };

  const extra = (
    <div className="extra-button-container">
      <Button
        size="small"
        htmlType="submit"
        style={{ marginRight: 5 }}
        onClick={() => {
          form.resetFields();
          navigate(`/digitalization/finishing/hourly-${type}`);
        }}
      >
        {" "}
        Back
      </Button>
      <Button htmlType="submit" type="primary" size="small" onClick={() => form.submit()}>
        {" "}
        Save
      </Button>
    </div>
  );

  const calculateTotalQuantity = (values) => {
    if (!values?.sewingEntries) return 0;
    return values.sewingEntries.reduce((sum, entry) => sum + (entry.quantity || 0), 0);
  };

  // Update form onChange handler
  const handleFormChange = (changedValues, allValues) => {
    const newTotal = calculateTotalQuantity(allValues);
    setTotalQuantity(newTotal);
  };

  useEffect(() => {
    if (id) {
      const fetchSewingDetails = async () => {
        try {
          const response = await getData(`/api/digitalization/single-group-wise-${type}-update-view`, null, payload);
          const data = response.data?.data;

          setSewingDetails(data);
          setSewingEntries(data.records);
          setTotalQuantity(data.total_qty);

          form.setFieldsValue({
            sewingEntries: data.records.map((item) => ({
              id: item.id,
              entry_time: `${item.hour_time_range_from} - ${item.hour_time_range_to}`,
              quantity: item.quantity,
              operator_name:
                item.operator_info?.name + " (" + item.operator_info?.additional_info?.id_card_no + ")" || "N/A",
            })),
          });
        } catch (error) {
          message.error("Failed to fetch details");
          setSewingDetails(null);
        }
      };
      fetchSewingDetails();
    }
  }, [id, form]);

  return (
    <>
      <AppPageHeader title={<Typography.Title level={5}>Update Hourly {type} Entry</Typography.Title>} extra={extra} />

      <Layout.Content className="item-details">
        <Card title="Entry Details" bordered={false} style={{ marginTop: 5 }}>
          {sewingDetails && (
            <Descriptions column={4}>
              <Descriptions.Item label="Finishing Line">{sewingDetails.finishing_line?.name}</Descriptions.Item>
              <Descriptions.Item label="Supervisor">{sewingDetails.supervisor?.name}</Descriptions.Item>
              <Descriptions.Item label="Style">{sewingDetails.style?.style_no}</Descriptions.Item>
              <Descriptions.Item label="Style Description">{sewingDetails.style?.description}</Descriptions.Item>
              <Descriptions.Item label="Sub Style">{sewingDetails.sub_style?.sub_style_no}</Descriptions.Item>
              <Descriptions.Item label="Sales Contract">{sewingDetails.sales_contract?.reference_no}</Descriptions.Item>
              <Descriptions.Item label="Buying Office">{sewingDetails.buying_office?.name}</Descriptions.Item>
              <Descriptions.Item label="Buyer">{sewingDetails.buyer?.name}</Descriptions.Item>
              <Descriptions.Item label="PO">{sewingDetails.po_map?.original_po}</Descriptions.Item>
              <Descriptions.Item label="Color">{sewingDetails.color?.name}</Descriptions.Item>
              <Descriptions.Item label="Size">{sewingDetails.size?.name}</Descriptions.Item>
              <Descriptions.Item label="Total Quantity">{totalQuantity}</Descriptions.Item>
            </Descriptions>
          )}
        </Card>

        <Card title="Entry Details" style={{ marginTop: 20 }} bordered={false}>
          <Form form={form} layout="vertical" onFinish={handleSubmit} onValuesChange={handleFormChange}>
            <Form.List name="sewingEntries">
              {(fields) => (
                <>
                  {fields.map(({ key, name, fieldKey, ...restField }) => (
                    <Card key={key} style={{ marginTop: 10 }}>
                      <Row gutter={24} align="middle">
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, "operator_name"]}
                            fieldKey={[fieldKey, "operator_name"]}
                            label="Operator Name"
                          >
                            <Input disabled />
                          </Form.Item>
                        </Col>

                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, "entry_time"]}
                            fieldKey={[fieldKey, "entry_time"]}
                            label="Time Range"
                          >
                            <Input disabled />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, "quantity"]}
                            fieldKey={[fieldKey, "quantity"]}
                            label="Quantity"
                            rules={[
                              {
                                required: true,
                                message: "Quantity is required",
                              },
                            ]}
                          >
                            <InputNumber style={{ width: "100%" }} />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                </>
              )}
            </Form.List>
            <Form.Item style={{ marginTop: 20 }}>
              <Button type="primary" htmlType="submit">
                Submit
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Layout.Content>
    </>
  );
};

export default UpdateFinishingHourlyIronOriginal;
